import { Eye, Mail } from 'lucide-react';
import { z } from 'zod';
import { StatusBadge } from './Badge';
import { Button } from './Button';
import { DataValue } from './DataValue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { formatValue } from './DataValue';
import { EmptyState } from './EmptyState';
import { Field } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { ListRow } from './List';
import { Select } from './Select';
import { ShareRecipientActions } from './ShareRecipientActions';
import { Loading } from './Spinner';
import { Text } from './Text';

const FORM_ID = 'share-record-form';

export type ShareRecordKind = 'CALL' | 'IMPOUND';
export type ShareRecordRecipientStatus = 'SENT' | 'VIEWED' | 'REVOKED';
export type ShareRecordExpiry = 'never' | '7d' | '30d';

export interface ShareRecordRecipient {
  id: string;
  email: string;
  status: ShareRecordRecipientStatus;
  /** When they first opened the record, if they have. */
  firstViewedAt: string | null;
  /** When they last opened the record, if they have. */
  lastViewedAt: string | null;
  /** Visits, counted at most once per half hour per session. */
  viewCount: number;
  /** When the owner added this address: the "Sent" time on the row. */
  createdAt: string;
}

const EXPIRY_OPTIONS: { value: ShareRecordExpiry; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/** "Ends Oct 27, 2026", so the saved date is never hidden behind the nearest option. */
function expiryDescription(expiresAt: string | null | undefined): string {
  if (!expiresAt) return 'Picking a number of days counts from today.';
  return `Ends ${formatValue(expiresAt, 'date')}. Picking a number of days counts from today.`;
}

/** The one field the dialog collects: an address to send a link to. */
export const shareRecipientSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter an email address.')
    .email('Enter a valid email address.'),
});

export type ShareRecipientFormValues = z.infer<typeof shareRecipientSchema>;

export interface ShareRecordDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the record in the title: "Share this call" or "Share this impound". */
  kind: ShareRecordKind;
  /** From `useZodForm(shareRecipientSchema, …)`. */
  form: ZodForm<typeof shareRecipientSchema>;
  recipients: ShareRecordRecipient[];
  /** The share is still being fetched: a spinner stands in for the list. */
  loading?: boolean;
  /** A link is being sent to the typed address. */
  sending?: boolean;
  /** The recipient whose resend or remove is in flight; its row locks. */
  busyRecipientId?: string | null;
  expiry: ShareRecordExpiry;
  /** The date the share actually ends, shown under the control. Null: never. */
  expiresAt?: string | null;
  onExpiryChange: (expiry: ShareRecordExpiry) => void;
  onSend: (values: ShareRecipientFormValues) => void;
  onResend: (recipientId: string) => void;
  onRemove: (recipientId: string) => void;
  /**
   * Open the record as the recipients see it. Given only once a share
   * exists; without it there is no Preview button.
   */
  onPreview?: () => void;
}

const RECIPIENT_LABEL: Record<ShareRecordRecipientStatus, string> = {
  SENT: 'Sent',
  VIEWED: 'Viewed',
  REVOKED: 'Revoked',
};

const RECORD_NOUN: Record<ShareRecordKind, string> = {
  CALL: 'call',
  IMPOUND: 'impound',
};

/**
 * The share dialog on a call or an impound: an address to send a link to,
 * how long the share lasts, and everyone it has gone to with whether they
 * have opened it. Resend and Remove sit on each row.
 *
 *   const form = useZodForm(shareRecipientSchema, { defaultValues: { email: '' } });
 *   <ShareRecordDialogView open={open} onOpenChange={setOpen} kind="CALL"
 *     form={form} recipients={share?.recipients ?? []} expiry={expiry}
 *     onExpiryChange={setExpiry} onSend={send} onResend={resend} onRemove={remove} />
 *
 * The container owns the requests and reports a refused send through
 * `form.setError('email', …)`; a refused resend is a toast, since there is
 * no field to put it under. Pass `onPreview` once the share exists and a
 * Preview button in the footer opens the record as the recipients see it.
 */
export function ShareRecordDialogView({
  open,
  onOpenChange,
  kind,
  form,
  recipients,
  loading = false,
  sending = false,
  busyRecipientId = null,
  expiry,
  expiresAt,
  onExpiryChange,
  onSend,
  onResend,
  onRemove,
  onPreview,
}: ShareRecordDialogViewProps) {
  const noun = RECORD_NOUN[kind];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share this {noun}</DialogTitle>
          <DialogDescription>
            People you add get an email with a one-time link. Removing them ends
            their access.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={6}>
          <Form form={form} gap={3} id={FORM_ID} onSubmit={onSend}>
            <FormField form={form} name="email" label="Email">
              {(field) => (
                <Inline gap={2} align="start">
                  <Stack gap={0} grow>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="off"
                      placeholder="name@example.com"
                    />
                  </Stack>
                  <Button
                    form={FORM_ID}
                    type="submit"
                    loading={sending}
                    className="shrink-0"
                  >
                    Send link
                  </Button>
                </Inline>
              )}
            </FormField>
          </Form>

          <Field
            label="Link expires"
            description={expiryDescription(expiresAt)}
          >
            <Select
              options={EXPIRY_OPTIONS}
              value={expiry}
              onValueChange={(value) =>
                onExpiryChange(value as ShareRecordExpiry)
              }
            />
          </Field>

          {loading ? (
            <Loading size="md" pad="sm" />
          ) : recipients.length === 0 ? (
            <EmptyState
              size="sm"
              icon={Mail}
              title="No one yet"
              description={`Add an email above to share this ${noun}.`}
            />
          ) : (
            <Stack divided>
              {recipients.map((recipient) => (
                <ShareRecipientRow
                  key={recipient.id}
                  recipient={recipient}
                  busy={busyRecipientId === recipient.id}
                  onResend={() => onResend(recipient.id)}
                  onRemove={() => onRemove(recipient.id)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {onPreview ? (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              leadingIcon={<Eye />}
              onClick={onPreview}
            >
              Preview
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface ShareRecipientRowProps {
  recipient: ShareRecordRecipient;
  busy: boolean;
  onResend: () => void;
  onRemove: () => void;
}

function ShareRecipientRow({
  recipient,
  busy,
  onResend,
  onRemove,
}: ShareRecipientRowProps) {
  const revoked = recipient.status === 'REVOKED';
  // View history stays on a removed row: the owner still wants to know
  // whether the person saw it before access ended.
  const viewed = !!recipient.lastViewedAt;
  return (
    <ListRow inset="y" wrap>
      <Stack gap={0} grow>
        <Text variant="body-sm-strong" truncate>
          {recipient.email}
        </Text>
        <Text variant="caption" tone="muted">
          Sent <DataValue value={recipient.createdAt} format="datetime" />
          {viewed ? (
            <>
              {' · '}Viewed{' '}
              <DataValue value={recipient.lastViewedAt} format="datetime" />
              {recipient.viewCount > 1 ? (
                <>
                  {' '}
                  ({recipient.viewCount} views, first{' '}
                  <DataValue
                    value={recipient.firstViewedAt ?? recipient.lastViewedAt}
                    format="datetime"
                  />
                  )
                </>
              ) : null}
            </>
          ) : null}
        </Text>
      </Stack>
      <StatusBadge
        status={recipient.status}
        label={RECIPIENT_LABEL[recipient.status]}
      />
      {revoked ? null : (
        <ShareRecipientActions
          email={recipient.email}
          busy={busy}
          onResend={onResend}
          onRemove={onRemove}
        />
      )}
    </ListRow>
  );
}
